import {
	Banner,
	Button,
	Placeholder,
	Spinner,
	Title,
} from '@telegram-apps/telegram-ui'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useNavigate } from 'react-router'
import { useMobileTrpc } from 'shared/api'
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from 'shared/components/ui/alert-dialog'
import { formatUzsAmount } from 'shared/lib/format'
import { useTranslate } from 'shared/lib/i18n'
import {
	formatLine,
	mapPurchaseError,
	purchaseAmountUzs,
	shortfallUzs,
	totalQuantity as sumQuantities,
	type PurchaseLine,
} from '../lib/purchase'

export type { PurchaseLine }

export function PurchasePanel({
	lotId,
	lotTitle,
	lines,
	unitPriceUzs,
	outcome,
	onPurchaseOutcome,
	onOutcomeDismiss,
}: {
	lotId: string
	lotTitle: string
	lines: PurchaseLine[]
	unitPriceUzs: number
	outcome: { type: 'success' | 'error'; message: string } | null
	onPurchaseOutcome: (outcome: {
		type: 'success' | 'error'
		message: string
	}) => void
	onOutcomeDismiss: () => void
}) {
	const trpc = useMobileTrpc()
	const navigate = useNavigate()
	const { t } = useTranslate()
	const [confirmOpen, setConfirmOpen] = useState(false)
	// One key per selection: a network retry replays the same purchase rather
	// than buying it a second time. The parent re-mounts this panel (keyed on
	// the selection) whenever the selection changes, so the key resets with it.
	const [idemKey, setIdemKey] = useState(() => crypto.randomUUID())
	const totalQuantity = sumQuantities(lines)
	const amountUzs = purchaseAmountUzs(lines, unitPriceUzs)
	const summary = lines.map(formatLine).join(', ')

	const { data: wallet, isLoading } = useQuery(trpc.wallet.Get.queryOptions())
	const buy = useMutation(trpc.purchases.Buy.mutationOptions())

	const balance = wallet?.balanceUzs ?? 0
	const shortfall = shortfallUzs(amountUzs, balance)

	async function handleBuy() {
		try {
			await buy.mutateAsync({
				lotId,
				items: lines.map((l) => ({
					inventoryId: l.inventoryId,
					quantity: l.quantity,
				})),
				expectedUnitPriceUzs: unitPriceUzs,
				idempotencyKey: idemKey,
			})
			onPurchaseOutcome({
				type: 'success',
				message: t('pages.purchase.successMessage'),
			})
		} catch (e) {
			const failure = mapPurchaseError(e)
			// The selection must change before the next attempt, so the reused
			// idempotency key would clash with a different payload — regenerate it.
			if (failure.regenerateIdempotencyKey) setIdemKey(crypto.randomUUID())
			const message = t(failure.messageKey)
			onPurchaseOutcome({ type: 'error', message })
		}
	}

	return (
		<section className='mt-2 rounded-xl bg-secondary/40 p-4'>
			{outcome ? (
				<Placeholder
					header={
						outcome.type === 'success'
							? t('pages.purchase.success')
							: t('pages.purchase.failed')
					}
					description={outcome.message}
					action={
						<Button size='l' stretched onClick={onOutcomeDismiss}>
							{t('common.done')}
						</Button>
					}
				/>
			) : (
				<>
					<Title level='3' weight='2' className='mb-2 text-foreground'>
						{t('pages.purchase.paymentForItems', { items: summary })}
					</Title>

					<Banner
						type='section'
						header={t('pages.purchase.amountDue', {
							amount: t('common.amountUzs', {
								amount: formatUzsAmount(amountUzs),
							}),
						})}
					>
						<div className='space-y-4'>
							<div>
								{t('pages.purchase.walletBalance', {
									amount: t('common.amountUzs', {
										amount: formatUzsAmount(balance),
									}),
								})}
							</div>
							{!isLoading && shortfall > 0 && (
								<div>
									<div className='font-medium'>
										{t('pages.purchase.shortfall', {
											amount: t('common.amountUzs', {
												amount: formatUzsAmount(shortfall),
											}),
										})}
									</div>
									<div>{t('pages.purchase.topupHint')}</div>
								</div>
							)}
						</div>
					</Banner>

					{isLoading && (
						<div className='flex justify-center py-6'>
							<Spinner size='m' />
						</div>
					)}

					{!isLoading && shortfall > 0 && (
						<div className='mt-4'>
							<Button size='l' stretched onClick={() => navigate('/wallet')}>
								{t('pages.purchase.topupWallet')}
							</Button>
						</div>
					)}

					{!isLoading && shortfall === 0 && (
						<div className='mt-4'>
							<Button
								size='l'
								stretched
								loading={buy.isPending}
								disabled={buy.isPending || totalQuantity === 0}
								onClick={() => setConfirmOpen(true)}
							>
								{t('pages.purchase.payFromBalance')}
							</Button>
						</div>
					)}
				</>
			)}

			<AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>
							{t('pages.purchase.confirmTitle')}
						</AlertDialogTitle>
						<AlertDialogDescription>
							{t('pages.purchase.confirmDescription')}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<div className='space-y-2 text-sm'>
						<div className='flex justify-between gap-4'>
							<span className='text-muted-foreground'>
								{t('pages.purchase.lotLabel')}
							</span>
							<span className='text-right font-medium'>{lotTitle}</span>
						</div>
						<div className='flex justify-between gap-4'>
							<span className='text-muted-foreground'>
								{t('pages.purchase.itemsLabel')}
							</span>
							<span className='text-right font-medium'>{summary}</span>
						</div>
						<div className='flex justify-between gap-4'>
							<span className='text-muted-foreground'>
								{t('pages.purchase.priceLabel')}
							</span>
							<span className='font-medium'>
								{t('common.amountUzs', { amount: formatUzsAmount(amountUzs) })}
							</span>
						</div>
					</div>
					<AlertDialogFooter>
						<AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
						<AlertDialogAction
							onClick={() => {
								setConfirmOpen(false)
								void handleBuy()
							}}
						>
							{t('common.confirm')}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</section>
	)
}
